/**
 * Chat API - REST client for internal chat
 * Endpoint: /api/v1/chat
 */

import type { Chat, ChatMessage, ChatUser } from '@/pages/chat/types';
import { api } from './client';

// ============================================
// TYPES
// ============================================

export interface ApiConversation {
  id: string;
  type: 'direct' | 'group';
  participantIds: string[];
  lastMessageId?: string;
  lastMessageAt?: string;
  createdAt: string;
  updatedAt: string;
  tenantId: string;
  // Optional enriched fields (if backend populates them)
  name?: string;
  participantNames?: Record<string, string>;
  unreadCount?: number;
}

export interface ApiChatMessage {
  id: string;
  conversationId: string;
  senderId: string;
  senderName?: string;
  content: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  tenantId: string;
}

// ============================================
// MAPPERS
// ============================================

export function mapConversationToChat(conv: ApiConversation): Chat {
  return {
    id: conv.id,
    type: conv.type,
    name: conv.name ?? `Conversation ${conv.id.slice(-6)}`,
    participants: conv.participantIds.map(
      pid =>
        ({
          id: pid,
          name: conv.participantNames?.[pid] ?? pid,
          status: 'offline' as const,
        }) satisfies ChatUser
    ),
    unreadCount: conv.unreadCount ?? 0,
    updatedAt: conv.lastMessageAt ?? conv.updatedAt,
  };
}

export function mapApiMessageToChatMessage(
  msg: ApiChatMessage,
  currentUserId: string
): ChatMessage {
  return {
    id: msg.id,
    chatId: msg.conversationId,
    senderId: msg.senderId,
    senderName: msg.senderName ?? msg.senderId,
    content: msg.content,
    timestamp: msg.createdAt,
    status: (msg.status as ChatMessage['status']) ?? 'sent',
    isOwn: msg.senderId === currentUserId,
  };
}

// ============================================
// API FUNCTIONS
// ============================================

export async function getConversations(): Promise<ApiConversation[]> {
  const response = await api.get<ApiConversation[] | { data: ApiConversation[] }>(
    '/chat/conversations'
  );
  return Array.isArray(response) ? response : (response as { data: ApiConversation[] }).data;
}

export async function getConversationMessages(
  conversationId: string,
  limit = 50
): Promise<ApiChatMessage[]> {
  const response = await api.get<ApiChatMessage[] | { data: ApiChatMessage[] }>(
    `/chat/conversations/${conversationId}/messages?limit=${limit}`
  );
  return Array.isArray(response) ? response : (response as { data: ApiChatMessage[] }).data;
}

export async function sendMessage(
  conversationId: string,
  content: string
): Promise<ApiChatMessage> {
  const response = await api.post<ApiChatMessage>('/chat/messages', {
    conversationId,
    content,
  });
  return response;
}

export async function startConversation(
  participantIds: string[],
  options?: { initialMessage?: string; name?: string }
): Promise<ApiConversation> {
  const body: { participantIds: string[]; initialMessage?: string; name?: string } = {
    participantIds,
  };
  if (options?.initialMessage) body.initialMessage = options.initialMessage;
  if (options?.name) body.name = options.name;
  const response = await api.post<ApiConversation>('/chat/conversations', body);
  return response;
}

export async function markConversationAsRead(conversationId: string): Promise<void> {
  await api.post<void>(`/chat/conversations/${conversationId}/read`, {});
}

export interface ApiChatUser {
  id: string;
  name: string;
  email?: string;
  avatarUrl?: string;
}

export async function getChatUsers(): Promise<ApiChatUser[]> {
  const response = await api.get<ApiChatUser[] | { data: ApiChatUser[] }>(
    '/users?status=ACTIVE&limit=100'
  );
  return Array.isArray(response) ? response : (response as { data: ApiChatUser[] }).data;
}
